import {
  type IUser,
  useFeatureFlag as useConfigCatFeatureFlag,
} from 'configcat-react';
import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCurrentUserContext } from '@/contexts';
// Imported from the concrete hook file rather than the '@/pages/Schedule/contexts'
// barrel: that barrel also re-exports `ScheduleContextProvider` and the
// generated schedule GraphQL hooks, and useFeatureFlag is statically imported
// by ~150 files across the app — including public, unauthenticated /page/*
// routes that never touch Schedule. A static import of the barrel pulls that
// whole module graph into a shared chunk that then gets modulepreloaded on
// every route, tripping Sentry's "Large Render Blocking Asset" check (Sentry
// WEB-CLIENT-2A1 / 2A4 / 2A7). Importing the leaf module keeps this hook's
// footprint to just the React context object.
import { useUnsafeScheduleContext } from '@/pages/Schedule/contexts/ScheduleContext/hooks/useScheduleContext';
import { type FeatureFlag, defaultFeatureValues } from './defaults';

// Stable identifier for anonymous (logged-out) visitors. It MUST be stable
// across renders: minting a fresh id per render makes ConfigCat treat every
// render as a new user and re-evaluate the flag, which re-renders and mints
// another id — an infinite render loop that freezes the page for logged-out
// users. Persisting it also keeps flag bucketing consistent across reloads.
const ANONYMOUS_ID_KEY = '_ah_anonymous_id';

const getAnonymousId = (): string => {
  try {
    const existing = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (existing) {
      return existing;
    }
    const id = uuidv4();
    localStorage.setItem(ANONYMOUS_ID_KEY, id);
    return id;
  } catch {
    return uuidv4();
  }
};

const anonymousId = getAnonymousId();

export const useFeatureFlag = <T extends FeatureFlag>(feature: T) => {
  const { currentUser, isLoading: isLoadingUser } = useCurrentUserContext();
  const scheduleContext = useUnsafeScheduleContext();
  const defaultValue = defaultFeatureValues[feature];

  const scheduleId = scheduleContext?.scheduleId ?? '';
  const userId = currentUser?.id;
  const userEmail = currentUser?.email;
  const userStatus = currentUser?.status;
  const campaign = currentUser?.campaign;

  // Memoize on the underlying values (not the currentUser object identity) so
  // ConfigCat receives a stable user and only re-evaluates when the identity
  // actually changes.
  const user = useMemo<IUser>(() => {
    const customProps = {
      campaign: campaign ?? '',
      currentScheduleId: scheduleId,
    };

    return userId
      ? {
          identifier: userId,
          email: userEmail ?? userId,
          custom: {
            ...customProps,
            userStatus: userStatus ?? 'public',
          },
        }
      : {
          identifier: anonymousId,
          email: `${anonymousId}@anonymous.user`,
          custom: {
            ...customProps,
            userStatus: 'public',
          },
        };
  }, [userId, userEmail, userStatus, campaign, scheduleId]);

  const result = useConfigCatFeatureFlag(feature, defaultValue, user);

  // While the profile query is in flight, `currentUser` is undefined and the
  // memo above evaluates against the synthetic anonymous identity — a complete
  // but wrong user, so any rule targeting email / user id / userStatus resolves
  // to its non-matching branch. ConfigCat's own `loading` covers only its first
  // fetch and is already false across the anonymous→authenticated identity
  // swap, so callers gating on it would act on a value we know is provisional.
  return { ...result, loading: result.loading || isLoadingUser };
};
